import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API } from '../config/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  FileText, Calendar, Plus, Clock, MapPin, DollarSign,
  Paintbrush, Hammer, Trees, Zap, Layers, Building, Wrench, Sparkles, Home as HomeIcon, ArrowRight,
  Loader2, X, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const serviceIcons = {
  decorating: Paintbrush,
  carpentry: Hammer,
  outdoors: Trees,
  electrics: Zap,
  plastering: Layers,
  building: Building,
  general: Wrench,
  cleaning: Sparkles,
  landlord: HomeIcon
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  pending_review: 'bg-orange-100 text-orange-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  inspection_required: 'bg-blue-100 text-blue-800',
  booked: 'bg-purple-100 text-purple-800',
  confirmed: 'bg-green-100 text-green-800',
  completed: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-red-100 text-red-800'
};

export const Dashboard = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  
  const [quotes, setQuotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [cancelBookingId, setCancelBookingId] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/dashboard' } } });
    }
  }, [isAuthenticated, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    try {
      const [quotesRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/quotes`),
        axios.get(`${API}/bookings`)
      ]);
      setQuotes(quotesRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  const handleCancelBooking = async () => {
    if (!cancelBookingId) return;
    
    setCancelling(true);
    try {
      await axios.delete(`${API}/bookings/${cancelBookingId}`);
      toast.success('Booking cancelled successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel booking');
    } finally {
      setCancelling(false);
      setCancelBookingId(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const pendingQuotes = quotes.filter(q => q.status === 'pending' || q.status === 'approved');
  const pendingReviewQuotes = quotes.filter(q => q.status === 'pending_review');
  const upcomingBookings = bookings.filter(b => b.status === 'confirmed');

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-muted/30 py-8" data-testid="dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-['Outfit']">
              Welcome, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-muted-foreground">Manage your quotes and bookings</p>
          </div>
          <Button 
            className="btn-accent"
            onClick={() => navigate('/quote')}
            data-testid="new-quote-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Quote
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="tab-quotes">
              Quotes ({quotes.length})
            </TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">
              Bookings ({bookings.length})
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="border-border">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold font-['Outfit']">{pendingQuotes.length}</p>
                      <p className="text-sm text-muted-foreground">Pending Quotes</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-border">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold font-['Outfit']">{upcomingBookings.length}</p>
                      <p className="text-sm text-muted-foreground">Upcoming Bookings</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-border">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold font-['Outfit']">
                        £{bookings.reduce((sum, b) => sum + b.total_cost, 0).toFixed(0)}
                      </p>
                      <p className="text-sm text-muted-foreground">Total Spent</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pending Quotes */}
              <Card className="border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-['Outfit']">Pending Quotes</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('quotes')}>
                    View All
                  </Button>
                </CardHeader>
                <CardContent>
                  {pendingQuotes.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No pending quotes</p>
                      <Button 
                        variant="link" 
                        onClick={() => navigate('/quote')}
                        className="mt-2"
                      >
                        Get your first quote
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingQuotes.slice(0, 3).map((quote) => {
                        const ServiceIcon = serviceIcons[quote.service_id] || Wrench;
                        return (
                          <div 
                            key={quote.id}
                            className="flex items-center justify-between p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                            onClick={() => navigate(`/booking/${quote.id}`)}
                            data-testid={`pending-quote-${quote.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                <ServiceIcon className="w-5 h-5 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium">{quote.service_name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {format(parseISO(quote.created_at), 'MMM d, yyyy')}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold">£{quote.total_cost.toFixed(2)}</p>
                              <Button size="sm" variant="ghost" className="h-auto p-0 text-primary">
                                Book <ArrowRight className="w-3 h-3 ml-1" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Upcoming Bookings */}
              <Card className="border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-['Outfit']">Upcoming Bookings</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('bookings')}>
                    View All
                  </Button>
                </CardHeader>
                <CardContent>
                  {upcomingBookings.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No upcoming bookings</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {upcomingBookings.slice(0, 3).map((booking) => (
                        <div 
                          key={booking.id}
                          className="p-4 rounded-xl bg-muted/50"
                          data-testid={`upcoming-booking-${booking.id}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-medium">{booking.service_name}</p>
                            <Badge className={statusColors[booking.status]}>
                              {booking.status}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {format(parseISO(booking.booking_date), 'MMM d')} at {booking.booking_time}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            {quotes.length === 0 ? (
              <Card className="border-border">
                <CardContent className="py-16 text-center">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-xl font-semibold mb-2 font-['Outfit']">No quotes yet</h3>
                  <p className="text-muted-foreground mb-4">Get started by requesting your first quote</p>
                  <Button onClick={() => navigate('/quote')} data-testid="get-first-quote-btn">
                    Get Quote <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {quotes.map((quote) => {
                  const ServiceIcon = serviceIcons[quote.service_id] || Wrench;
                  const canBook = quote.status === 'approved' || quote.status === 'pending';
                  const isPendingReview = quote.status === 'pending_review';
                  return (
                    <Card key={quote.id} className="border-border hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                              <ServiceIcon className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <h3 className="font-semibold font-['Outfit']">{quote.service_name}</h3>
                              <p className="text-sm text-muted-foreground">
                                {format(parseISO(quote.created_at), 'MMM d, yyyy')}
                              </p>
                            </div>
                          </div>
                          <Badge className={statusColors[quote.status]}>
                            {quote.status === 'pending_review' ? 'Under Review' : quote.status.replace('_', ' ')}
                          </Badge>
                        </div>
                        
                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                          {quote.description}
                        </p>
                        
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                          <MapPin className="w-4 h-4" />
                          <span className="truncate">{quote.address}</span>
                        </div>
                        
                        <div className="flex items-center justify-between pt-4 border-t border-border">
                          <div>
                            <p className="text-sm text-muted-foreground">
                              {quote.price_min && quote.price_max ? 'Estimated Range' : 'Estimated Cost'}
                            </p>
                            <p className="text-xl font-bold text-primary font-['Outfit']">
                              {quote.price_min && quote.price_max 
                                ? `£${quote.price_min.toFixed(0)} - £${quote.price_max.toFixed(0)}`
                                : `£${quote.total_cost.toFixed(2)}`
                              }
                            </p>
                          </div>
                          {isPendingReview ? (
                            <span className="text-sm text-orange-600 flex items-center gap-1">
                              <Clock className="w-4 h-4" /> Awaiting review
                            </span>
                          ) : canBook && quote.status !== 'booked' ? (
                            <Button 
                              className="btn-accent"
                              onClick={() => navigate(`/booking/${quote.id}`)}
                              data-testid={`book-quote-${quote.id}`}
                            >
                              Book Now
                            </Button>
                          ) : null}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            {bookings.length === 0 ? (
              <Card className="border-border">
                <CardContent className="py-16 text-center">
                  <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-xl font-semibold mb-2 font-['Outfit']">No bookings yet</h3>
                  <p className="text-muted-foreground mb-4">Book a service from your quotes</p>
                  <Button onClick={() => setActiveTab('quotes')} data-testid="view-quotes-btn">
                    View Quotes
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking) => (
                  <Card key={booking.id} className="border-border">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-xl bg-primary/10 flex flex-col items-center justify-center">
                            <span className="text-lg font-bold text-primary">
                              {format(parseISO(booking.booking_date), 'd')}
                            </span>
                            <span className="text-xs text-primary uppercase">
                              {format(parseISO(booking.booking_date), 'MMM')}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-semibold font-['Outfit']">{booking.service_name}</h3>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {booking.booking_time}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {booking.address}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <Badge className={statusColors[booking.status]}>
                              {booking.status}
                            </Badge>
                            <p className="text-lg font-bold mt-1 font-['Outfit']">
                              £{booking.total_cost.toFixed(2)}
                            </p>
                          </div>
                          {booking.status === 'confirmed' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setCancelBookingId(booking.id)}
                              data-testid={`cancel-booking-${booking.id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {booking.notes && (
                        <div className="mt-4 pt-4 border-t border-border">
                          <p className="text-sm text-muted-foreground">
                            <strong>Notes:</strong> {booking.notes}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Cancel Booking Dialog */}
      <AlertDialog open={!!cancelBookingId} onOpenChange={() => setCancelBookingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Cancel Booking?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this booking? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-dialog-cancel">Keep Booking</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelBooking}
              className="bg-destructive hover:bg-destructive/90"
              disabled={cancelling}
              data-testid="cancel-dialog-confirm"
            >
              {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Yes, Cancel'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
